import React, { useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import styles from './Modal.module.css';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: ModalSize;
  closeable?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeable = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeable && e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose, closeable]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Devolver o foco a quem abriu o modal é efeito do FECHAMENTO, e por isso mora
  // num effect que só depende de `isOpen`. Junto com o listener de Escape (que
  // depende de `handleKeyDown`, recriado sempre que o pai passa um `onClose`
  // inline) o cleanup rodava a cada re-render do pai e arrancava o foco do que
  // a pessoa estivesse usando — na prova, a cada tecla digitada na resposta.
  useEffect(() => {
    if (!isOpen || !closeable) return;
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    return () => {
      previousActiveRef.current?.focus?.();
      previousActiveRef.current = null;
    };
  }, [isOpen, closeable]);

  useEffect(() => {
    if (!isOpen || !closeable) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeable, handleKeyDown]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusable[0];
      if (first) {
        first.focus();
      }
    }
  }, [isOpen]);

  const generatedId = React.useId();
  const titleId = `modal-title-${generatedId.replace(/:/g, '')}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.overlay}
          onClick={handleOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={[styles.modal, styles[size]].join(' ')}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.header}>
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
              {closeable && (
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label="Fechar"
                >
                  <X size={20} />
                </button>
              )}
            </div>
            <div className={styles.body}>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
